import { startOfWeek } from 'date-fns';
import { Frown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { useViewModeAndDate } from '../../hooks/use-view-mode-and-date';
import { useWeekOverview } from '../../hooks/use-week-overview';
import { WeekGrid } from './week-grid';
import { WeekViewSkeleton } from './week-view-skeleton';

export function WeekView() {
  const { t } = useTranslation('authenticated.timeTracking');
  const { getErrorMessage } = useApiError();
  const { selectedDate } = useViewModeAndDate();
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });

  const { data, isLoading, isRefetching, error, refetch } = useWeekOverview({
    date: selectedDate,
  });

  if (isLoading) return <WeekViewSkeleton />;

  if (error) {
    return (
      <Empty className="rounded-lg border bg-card">
        <EmptyMedia variant="icon">
          <Frown />
        </EmptyMedia>
        <EmptyTitle>{t('weekView.error.title')}</EmptyTitle>
        <EmptyDescription>{getErrorMessage(error)}</EmptyDescription>
        <EmptyContent>
          <ButtonLoadable
            variant="outline"
            size="sm"
            showRefreshIcon
            isLoading={isRefetching}
            onClick={() => refetch()}
          >
            {t('weekView.error.retry')}
          </ButtonLoadable>
        </EmptyContent>
      </Empty>
    );
  }

  if (!data) return null;

  return (
    <WeekGrid summary={data.summary} dailyBreakdown={data.dailyBreakdown} weekStart={weekStart} />
  );
}
